import { useQuery } from '@tanstack/react-query';
import type { DashboardSnapshot, Weather } from '@daily/shared';
import { Cloud, CalendarDays, Camera, Music, Sun, CloudRain, CloudFog, CloudSnow, Zap, CloudSun, Flame } from 'lucide-react';
import { weatherTheme } from '@/lib/weatherTheme';
import { api, type NestState } from '@/lib/api';

// Weather chip label nudges to a pollen warning when it's high+, so a glance
// at the wall tells Jomi whether it's an antihistamine day.
function pollenLabel(weather: Weather): string {
  const level = weather.pollen?.level;
  if (level === 'high') return 'Islington · pollen high';
  if (level === 'very_high') return 'Islington · pollen v.high';
  return 'Islington';
}

interface AmbienceRailProps {
  dashboard: DashboardSnapshot;
  onMemoryClick?: () => void;
  onNowPlayingClick?: () => void;
  onWeatherClick?: () => void;
  onTodayClick?: () => void;
  onHeatingClick?: () => void;
}

// Quick summary of the household's heating state: count of zones currently
// calling for heat + the average ambient temp. Compact enough for a chip.
function heatingSummary(state?: NestState): { primary: string; secondary: string } {
  if (!state || state.zones.length === 0) {
    return { primary: 'Heating', secondary: 'tap to set up' };
  }
  const heating = state.zones.filter((z) => z.hvacStatus === 'HEATING').length;
  const temps = state.zones
    .map((z) => z.ambientTempC)
    .filter((t): t is number => typeof t === 'number');
  const avg = temps.length ? temps.reduce((a, b) => a + b, 0) / temps.length : null;
  const primary = avg !== null ? `${avg.toFixed(1)}°` : 'Heating';
  const secondary =
    heating === 0
      ? 'all idle'
      : heating === 1
        ? '1 zone heating'
        : `${heating} zones heating`;
  return { primary, secondary };
}

function WeatherIcon({ condition }: { condition: string }) {
  switch (condition) {
    case 'clear':
      return <Sun size={18} strokeWidth={2} />;
    case 'fair':
      return <CloudSun size={18} strokeWidth={2} />;
    case 'rain':
    case 'showers':
      return <CloudRain size={18} strokeWidth={2} />;
    case 'snow':
      return <CloudSnow size={18} strokeWidth={2} />;
    case 'fog':
      return <CloudFog size={18} strokeWidth={2} />;
    case 'thunder':
      return <Zap size={18} strokeWidth={2} />;
    default:
      return <Cloud size={18} strokeWidth={2} />;
  }
}

interface ChipProps {
  background: string;
  ink?: string;
  icon: React.ReactNode;
  label: string;
  primary: string;
  secondary?: string;
  onClick?: () => void;
}

function Chip({ background, ink = '#1A1410', icon, label, primary, secondary, onClick }: ChipProps) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className="relative rounded-md overflow-hidden shadow-sm border border-black/[0.04] p-3 flex flex-col gap-1 text-left w-full"
      style={{ background, color: ink }}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.14em] uppercase opacity-70">
        {icon}
        <span>{label}</span>
      </div>
      <div className="font-serif text-[24px] leading-none">{primary}</div>
      {secondary && (
        <div className="text-[11px] opacity-70 leading-tight truncate">{secondary}</div>
      )}
    </Tag>
  );
}

export function AmbienceRail({
  dashboard,
  onMemoryClick,
  onNowPlayingClick,
  onWeatherClick,
  onTodayClick,
  onHeatingClick,
}: AmbienceRailProps) {
  const { weather, agenda, nowPlaying, watchman } = dashboard;
  // Nest polls every 30s — fresh enough for "is the heating on" without
  // hammering Google. Hidden tabs pause to save quota.
  const nest = useQuery<NestState>({
    queryKey: ['nest', 'state'],
    queryFn: () => api.getNestState(),
    refetchInterval: () => (document.visibilityState === 'visible' ? 30_000 : false),
    staleTime: 15_000,
    retry: 1,
  });
  const heating = heatingSummary(nest.data);
  const weatherStyle = weatherTheme(weather.condition);
  const watchmanLabel =
    watchman.status === 'all_quiet'
      ? 'all quiet'
      : watchman.status === 'motion'
        ? 'motion'
        : 'offline';

  return (
    <div className="flex flex-col gap-3">
      <Chip
        background={weatherStyle.gradient}
        ink={weatherStyle.ink}
        icon={<WeatherIcon condition={weather.condition} />}
        label={pollenLabel(weather)}
        primary={`${weather.temperatureC}°`}
        secondary={weather.condition + (weather.sub ? ` · ${weather.sub}` : '')}
        onClick={onWeatherClick}
      />
      <Chip
        background="#FFFFFF"
        icon={<CalendarDays size={14} strokeWidth={2} />}
        label="Today"
        primary={agenda.length > 0 ? `${agenda.length} event${agenda.length > 1 ? 's' : ''}` : 'Open'}
        secondary={agenda[0]?.title}
        onClick={onTodayClick}
      />
      <Chip
        background="linear-gradient(135deg, #E94D2C 0%, #B83518 100%)"
        ink="#FFFFFF"
        icon={<Flame size={14} strokeWidth={2} />}
        label="Heating"
        primary={heating.primary}
        secondary={heating.secondary}
        onClick={onHeatingClick}
      />
      <Chip
        background="#0F1419"
        ink="#FFFFFF"
        icon={<Camera size={14} strokeWidth={2} />}
        label="Watchman"
        primary={watchmanLabel}
        secondary={watchman.zone}
      />
      <Chip
        background="linear-gradient(135deg, #1F2D44 0%, #5B2A4A 100%)"
        ink="#FFFFFF"
        icon={<Music size={14} strokeWidth={2} />}
        label={nowPlaying?.playing ? `Playing · ${nowPlaying.zone}` : 'Music'}
        primary={nowPlaying?.playing ? nowPlaying.track : 'Silence'}
        secondary={nowPlaying?.playing ? nowPlaying.artist : 'tap to pick a Sonos'}
        onClick={onNowPlayingClick}
      />
      {dashboard.memory && onMemoryClick && (
        <Chip
          background={`linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.7)), url(${dashboard.memory.photoUrl})`}
          ink="#FFFFFF"
          icon={<span aria-hidden>🕯</span>}
          label={`${dashboard.memory.yearDiff} years ago`}
          primary="On this day"
          secondary={dashboard.memory.caption}
          onClick={onMemoryClick}
        />
      )}
    </div>
  );
}
